import { formatINR, isPAN, isPhone } from "@templeos/shared";
import type { Cart } from "./cart.js";
import type { Payer, PosItem } from "./types.js";

/**
 * Quotas the POS checks before anything is printed (SOW Modules 1 and 6): each item's festival daily quota (limit
 * and sold at the last configuration load, less bills still waiting on this terminal) and the cashier's ticket quota
 * for the shift. Returns the first breach as a message, or null. The server re-checks both on upload.
 */
export function quotaBreach(cart: Cart, items: readonly PosItem[], pending: ReadonlyMap<string, number>,
  cashier: { max: number } | null, ticketsInShift: number): string | null {
  const wanted = new Map<string, number>();
  for (const l of cart) if (l.itemId) wanted.set(l.itemId, (wanted.get(l.itemId) ?? 0) + l.qty);
  for (const [itemId, qty] of wanted) {
    const item = items.find((i) => i.id === itemId);
    if (!item?.quota) continue;
    const left = quotaLeft(item, pending.get(itemId) ?? 0);
    if (qty > left) return `Today's quota for ${item.name} is ${item.quota.limit}; ${left} left.`;
  }
  const tickets = cart.filter((l) => l.kind === "ticket").reduce((n, l) => n + l.qty, 0);
  if (cashier && tickets && ticketsInShift + tickets > cashier.max) {
    return `Your ticket quota for this shift is ${cashier.max}; ${Math.max(0, cashier.max - ticketsInShift)} left. Ask the supervisor.`;
  }
  return null;
}

/** Units of an item still allowed today under its festival quota (Infinity when it has none). */
export const quotaLeft = (item: PosItem, pending: number): number => (item.quota ? Math.max(0, item.quota.limit - item.quota.sold - pending) : Infinity);

/** Tile note once an item's daily quota passes its alert level ("12 left today"); null below it. */
export function quotaAlert(item: PosItem, pending: number): string | null {
  if (!item.quota) return null;
  const used = item.quota.limit - quotaLeft(item, pending);
  return used * 100 >= item.quota.limit * item.quota.alertPct ? `${quotaLeft(item, pending)} left today` : null;
}

export type CashLevel = "ok" | "near" | "over";

/** Counter cash limit alert (SOW Module 2): warn from 90% of the limit, block-level alert above it. */
export function cashLevel(drawerCash: number, limit: number | null): CashLevel {
  if (!limit || limit <= 0) return "ok";
  if (drawerCash > limit) return "over";
  return drawerCash >= limit * 0.9 ? "near" : "ok";
}

/**
 * Prasad stock left on this counter after what is already in the bill and in bills still waiting on this terminal
 * (SOW Module 36). Unknown stock (the stock service unreachable) returns null and the server's stock trigger has the
 * final word.
 */
export function stockLeft(stock: ReadonlyMap<string, number> | null, itemId: string, inCart: number, pending = 0): number | null {
  if (!stock || !stock.has(itemId)) return null;
  return Math.max(0, (stock.get(itemId) ?? 0) - inCart - pending);
}

/** Income Tax limits from the temple profile (paise): PAN above this donation total, and the s.269ST cash limit. */
export interface TaxLimits { panAbove: number; cashLimit: number }
export const DEFAULT_TAX_LIMITS: TaxLimits = { panAbove: 5_000_000, cashLimit: 20_000_000 };

/** Devotee detail errors by field, shown inline and checked again before any bill is saved. */
export function payerErrors(payer: Payer, donation: number, limits: TaxLimits): { phone?: string; pan?: string } {
  const out: { phone?: string; pan?: string } = {};
  if (payer.phone?.trim() && !isPhone(payer.phone)) out.phone = "Enter a 10-digit mobile number.";
  if (payer.pan?.trim() && !isPAN(payer.pan)) out.pan = "PAN must look like ABCDE1234F.";
  else if (donation > limits.panAbove && !payer.pan?.trim()) out.pan = `PAN is required for donations above ${formatINR(limits.panAbove, { decimals: false })}.`;
  return out;
}

/**
 * Rules the server would refuse the bill for after the devotee has paid and left (receipts: PAN_REQUIRED,
 * CASH_LIMIT_269ST), checked before the bill is saved or printed. Returns the first problem, or null.
 */
export function billProblem(p: { payer: Payer; donation: number; cash: number }, limits: TaxLimits): string | null {
  const errors = payerErrors(p.payer, p.donation, limits);
  const field = errors.phone ?? errors.pan;
  if (field) return field;
  if (p.cash >= limits.cashLimit) {
    return `Cash of ${formatINR(limits.cashLimit, { decimals: false })} or more from one person is not allowed (Income Tax s.269ST). Take UPI or a split payment.`;
  }
  return null;
}

/** The server's password policy (SOW Module 72), checked here first so the cashier sees which field is wrong. */
export function passwordErrors(f: { current: string; next: string; again: string }, username: string): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.current) e.current = "Enter your current password.";
  if (f.next.length < 10) e.next = "At least 10 characters.";
  else if (!/[A-Za-z]/.test(f.next) || !/[0-9]/.test(f.next)) e.next = "Use both letters and numbers.";
  else if (username && f.next.toLowerCase().includes(username.toLowerCase())) e.next = "Must not contain your username.";
  if (!e.next && f.again !== f.next) e.again = "The new passwords do not match.";
  return e;
}
